import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, message, Tabs } from 'antd';
import GrowthValue from '../../../components/GrowthValue';
import { getYesterdaySummary, getYesterdayProductDetail, getYesterdayOnlineProductDetail } from '../../../services/api';

function SummaryCard({ title, yesterday, prevDay, growth, bgColor }) {
  return (
    <Card title={title} size="small" style={{ height: '100%', background: bgColor}}>
      <Row gutter={[16, 8]}>
        <Col span={8}>
          <Statistic
            title="昨日订单数"
            value={yesterday?.orderCount ?? 0}
            suffix={<span style={{ fontSize: 14, color: '#999', whiteSpace: 'nowrap' }}>前日: {prevDay?.orderCount ?? 0} <GrowthValue value={growth?.orderCount} /></span>}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="昨日销售金额"
            value={yesterday?.totalAmount ?? 0}
            precision={2}
            prefix="¥"
            suffix={<span style={{ fontSize: 14, color: '#999', whiteSpace: 'nowrap' }}>前日: ¥{(prevDay?.totalAmount ?? 0).toLocaleString()} <GrowthValue value={growth?.totalAmount} /></span>}
          />
        </Col>
      </Row>
    </Card>
  );
}

const productColumns = [
  { title: '产品编码', dataIndex: 'code', key: 'code', width: 140 },
  { title: '产品名称', dataIndex: 'name', key: 'name', width: 140 },
  {
    title: '昨日数量',
    dataIndex: 'yesterdayQty',
    key: 'yesterdayQty',
    width: 100,
    align: 'right',
    render: v => v.toLocaleString(),
    sorter: (a, b) => a.yesterdayQty - b.yesterdayQty,
    defaultSortOrder: 'descend',
  },
  {
    title: '昨日金额',
    dataIndex: 'yesterdayAmount',
    key: 'yesterdayAmount',
    width: 140,
    align: 'right',
    render: v => `¥${v.toLocaleString()}`,
    sorter: (a, b) => a.yesterdayAmount - b.yesterdayAmount,
  },
  {
    title: '前日数量',
    dataIndex: 'prevDayQty',
    key: 'prevDayQty',
    width: 100,
    align: 'right',
    render: v => v.toLocaleString(),
    sorter: (a, b) => a.prevDayQty - b.prevDayQty,
  },
  {
    title: '前日金额',
    dataIndex: 'prevDayAmount',
    key: 'prevDayAmount',
    width: 140,
    align: 'right',
    render: v => `¥${v.toLocaleString()}`,
    sorter: (a, b) => a.prevDayAmount - b.prevDayAmount,
  },
  {
    title: '数量对比',
    key: 'qtyGrowth',
    width: 100,
    align: 'center',
    render: (_, r) => {
      if (r.prevDayQty === 0) return r.yesterdayQty > 0 ? <GrowthValue value={100} /> : <GrowthValue value={0} />;
      return <GrowthValue value={Number(((r.yesterdayQty - r.prevDayQty) / r.prevDayQty * 100).toFixed(2))} />;
    },
  },
  {
    title: '金额对比',
    key: 'amountGrowth',
    width: 100,
    align: 'center',
    render: (_, r) => {
      if (r.prevDayAmount === 0) return r.yesterdayAmount > 0 ? <GrowthValue value={100} /> : <GrowthValue value={0} />;
      return <GrowthValue value={Number(((r.yesterdayAmount - r.prevDayAmount) / r.prevDayAmount * 100).toFixed(2))} />;
    },
  },
];

const onlineProductColumns = [
  { title: '店铺ID', dataIndex: 'shopId', key: 'shopId', width: 100 },
  { title: '店铺名称', dataIndex: 'shopName', key: 'shopName', width: 160 },
  { title: '产品名称', dataIndex: 'name', key: 'name', width: 200 },
  {
    title: '销售金额',
    dataIndex: 'amount',
    key: 'amount',
    width: 140,
    align: 'right',
    render: v => `¥${v.toLocaleString()}`,
    sorter: (a, b) => a.amount - b.amount,
    defaultSortOrder: 'descend',
  },
  {
    title: '商品销量',
    dataIndex: 'qty',
    key: 'qty',
    width: 100,
    align: 'right',
    render: v => v.toLocaleString(),
    sorter: (a, b) => a.qty - b.qty,
  },
];

function getYesterdayDate() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function YesterdaySummary() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [onlineProducts, setOnlineProducts] = useState([]);
  const [onlineProductLoading, setOnlineProductLoading] = useState(false);
  const [queryDate, setQueryDate] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const date = getYesterdayDate();
        const res = await getYesterdaySummary(date);
        setData(res.data);
        setQueryDate(res.data?.queryDate);
      } catch (err) {
        message.error(err.message);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setProductLoading(true);
      try {
        const date = getYesterdayDate();
        const res = await getYesterdayProductDetail(date);
        setProducts(
          (res.data?.products || []).map(p => ({
            key: p.code,
            code: p.code,
            name: p.name,
            yesterdayQty: p.yesterday.qty,
            yesterdayAmount: p.yesterday.amount,
            prevDayQty: p.prevDay.qty,
            prevDayAmount: p.prevDay.amount,
          }))
        );
        if (!queryDate) setQueryDate(res.data?.queryDate);
      } catch (err) {
        message.error(err.message);
      }
      setProductLoading(false);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchOnlineProducts = async () => {
      setOnlineProductLoading(true);
      try {
        const date = getYesterdayDate();
        const res = await getYesterdayOnlineProductDetail(date);
        setOnlineProducts(
          (res.data?.products || []).map((p, i) => ({
            key: i,
            shopId: p.shopId,
            shopName: p.shopName,
            name: p.name,
            amount: p.amount,
            qty: p.qty,
          }))
        );
      } catch (err) {
        message.error(err.message);
      }
      setOnlineProductLoading(false);
    };
    fetchOnlineProducts();
  }, []);

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 8 }}>
        {data && (
          <>
            {queryDate && <div style={{ textAlign: 'center', color: '#666', marginBottom: 12 }}>数据日期: {queryDate}</div>}
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <SummaryCard
                  title="线上 (数据来源：聚水潭ERP)"
                  yesterday={data.yesterday.online}
                  prevDay={data.prevDay.online}
                  growth={data.growth.online}
                  bgColor="#e6f7ff"
                />
              </Col>
              <Col span={8}>
                <SummaryCard
                  title="线下 (数据来源：金蝶云星空)"
                  yesterday={data.yesterday.offline}
                  prevDay={data.prevDay.offline}
                  growth={data.growth.offline}
                  bgColor="#fff7e6"
                />
              </Col>
              <Col span={8}>
                <SummaryCard
                  title="合计"
                  yesterday={data.yesterday.total}
                  prevDay={data.prevDay.total}
                  growth={data.growth.total}
                  bgColor="#f0f5ff"
                />
              </Col>
            </Row>
            <Card style={{ marginTop: 16 }}>
              <Tabs
                defaultActiveKey="online"
                items={[
                  {
                    key: 'online',
                    label: '昨日线上产品动销(数据来源：聚水潭ERP)',
                    children: (
                      <Table
                        columns={onlineProductColumns}
                        dataSource={onlineProducts}
                        loading={onlineProductLoading}
                        size="small"
                        pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
                        scroll={{ x: 700 }}
                      />
                    ),
                  },
                  {
                    key: 'offline',
                    label: '近两日产品动销明细（数据来源：金蝶云星空）',
                    children: (
                      <Table
                        columns={productColumns}
                        dataSource={products}
                        loading={productLoading}
                        size="small"
                        pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
                        scroll={{ x: 900 }}
                      />
                    ),
                  },
                ]}
              />
            </Card>
          </>
        )}
        {!data && !loading && (
          <Card><div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无数据</div></Card>
        )}
      </div>
    </Spin>
  );
}
